export default function CorporateLawPage() {
  return (
    <div className="min-h-screen bg-white font-playfair">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center justify-center w-full md:w-auto">
              <h1>
                <a href="/">
                  <img 
                    src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                    alt="VTC Legal - Fractional General Counsel for AI and Tech Companies" 
                    className="h-16 w-auto"
                  />
                </a>
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-10">
              <a href="/#services" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Services</a>
              <a href="/#why-vtc" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Why VTC</a>
              <a href="/#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">How It Works</a>
              <a href="/#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium">Get Started</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-8 font-playfair">
            Corporate Law
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed font-inter">
            Build a solid legal foundation that supports growth, attracts investment, and positions your company for long-term success.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">Foundation Matters</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  Poor corporate structure decisions made early can create expensive problems later. Fundraising difficulties, tax inefficiencies, and governance conflicts often trace back to inadequate legal foundations.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  We help technology companies build corporate structures that anticipate growth, facilitate investment, and maintain compliance as you scale.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">Investment Ready</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  Clean corporate records and proper governance structures are essential for fundraising success. Investors expect professional-grade documentation and compliant operations.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  Our corporate law services ensure your company is always ready for due diligence, whether for funding rounds, strategic partnerships, or acquisitions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Services */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-16 text-center font-playfair">Comprehensive Corporate Legal Services</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-2-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Entity Formation</h4>
              <p className="text-gray-700 font-inter mb-4">
                Delaware C-Corp setup optimized for venture funding, with proper tax elections and governance structure.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• Jurisdiction selection</li>
                <li>• Capital structure design</li>
                <li>• Tax optimization</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-pie-chart-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Equity Management</h4>
              <p className="text-gray-700 font-inter mb-4">
                Stock option plans, equity grants, and cap table management from formation through exit.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• 409A valuations</li>
                <li>• Employee stock plans</li>
                <li>• Equity compensation</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Board Governance</h4>
              <p className="text-gray-700 font-inter mb-4">
                Board structure, meeting protocols, and governance best practices that scale with growth.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• Board composition</li>
                <li>• Committee structure</li>
                <li>• Meeting management</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-funds-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Fundraising Support</h4>
              <p className="text-gray-700 font-inter mb-4">
                Due diligence preparation, term sheet negotiation, and investment documentation.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• Data room preparation</li>
                <li>• Investment agreements</li>
                <li>• Investor relations</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Compliance Management</h4>
              <p className="text-gray-700 font-inter mb-4">
                Ongoing corporate housekeeping, regulatory filings, and compliance monitoring.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• Annual reporting</li>
                <li>• Corporate resolutions</li>
                <li>• Record maintenance</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-exchange-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">M&A Preparation</h4>
              <p className="text-gray-700 font-inter mb-4">
                Exit readiness, acquisition support, and transaction structuring for strategic exits.
              </p>
              <ul className="text-gray-600 space-y-1 text-sm font-inter">
                <li>• Clean-up projects</li>
                <li>• Transaction documents</li>
                <li>• Post-closing integration</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Framework */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center font-playfair">Strategic Governance Framework</h3>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                  <i className="ri-rocket-line text-white text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Startup Stage (Pre-Series A)</h4>
                  <p className="text-gray-700 mb-4 font-inter">
                    Focus on founder-friendly structures that maintain control while preparing for institutional investment.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Delaware C-Corp formation</li>
                      <li>• Founder stock vesting</li>
                      <li>• Employee stock option plan</li>
                    </ul>
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Board of directors setup</li>
                      <li>• Basic governance policies</li>
                      <li>• IP assignment agreements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                  <i className="ri-line-chart-line text-white text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Growth Stage (Series A+)</h4>
                  <p className="text-gray-700 mb-4 font-inter">
                    Professional governance structures that balance investor protection with operational flexibility.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Investor board seats</li>
                      <li>• Audit & compensation committees</li>
                      <li>• Enhanced reporting requirements</li>
                    </ul>
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Liquidation preferences</li>
                      <li>• Anti-dilution protections</li>
                      <li>• Drag-along provisions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                  <i className="ri-trophy-line text-white text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Scale Stage (Pre-IPO/Exit)</h4>
                  <p className="text-gray-700 mb-4 font-inter">
                    Public company readiness or acquisition optimization with institutional-grade governance.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Independent board majority</li>
                      <li>• SOX compliance preparation</li>
                      <li>• Executive compensation plans</li>
                    </ul>
                    <ul className="text-gray-600 space-y-2 text-sm font-inter">
                      <li>• Clean cap table management</li>
                      <li>• Exit mechanism optimization</li>
                      <li>• Transaction readiness</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-8 font-playfair">
            Ready to Build Your Corporate Foundation?
          </h3>
          <p className="text-xl text-gray-300 mb-12 font-inter">
            Get strategic corporate law guidance that positions your company for growth and investment success.
          </p>
          <a href="/#contact" className="bg-teal-600 text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
            Schedule Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 text-gray-900 py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm font-inter">
            © 2026 VTC Legal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}